import React, { useEffect, useState } from 'react';
import { Button, Flex, Form, Input, message, Space, Row, Col, Select, Tag } from 'antd';
import { CloseOutlined, PlusOutlined, SwapOutlined } from '@ant-design/icons';
import { useRequest, useSetState } from 'ahooks';
import { commonApi, CompanyFieldOption, ECompanyFieldOptionType } from '@/axios/common';
import {
  articleTemplateApi,
  ArticleTemplateDetailRes,
  ArticleTemplateIndustry,
  UpsertArticleTemplateReq,
} from '@/axios/articleTemplate';
import CommonTable from '@/components/Table';
import { timeFormatToYearSecond } from '@/utils/date';
import { openDeleteModal } from '@/components/Modal/common';
import EllipsisText from '@/components/Table/EllipsisText';
import SearchInput from '@/components/SearchInput';
import { ColumnsType } from 'antd/es/table';
import CommonModalFactory from '@/plugins/ModalFactory';
import AccessButton from '@/components/AccessButton';
import ArticleTemplateCombinationEditor, {
  defaultCombinations,
  validateTemplateCombinations,
} from '@/components/Business/ArticleTemplateCombinationEditor';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import TemplateMediaReplacementDialog, { SelectedArticleTemplate } from './TemplateMediaReplacementDialog';

type IndustryOption = {
  label: string;
  value: string;
};

type ArticleTemplateListItem = Omit<UpsertArticleTemplateReq, 'industries'> & {
  industries?: ArticleTemplateIndustry[];
};

const getIndustryName = (industry?: ArticleTemplateIndustry | CompanyFieldOption) => {
  if (!industry) {
    return '';
  }

  const id = industry.industryId ?? industry.id;
  const displayValue =
    isValidIndustryId(id) && isValidIndustryId(industry.value) ? String(industry.value) : '';

  return industry.industriy || industry.industry || industry.name || industry.label || displayValue;
};

const getIndustryId = (industry?: ArticleTemplateIndustry | CompanyFieldOption) =>
  industry?.industryId ?? industry?.id ?? industry?.value;

const isValidIndustryId = (industryId?: string | number | null) =>
  industryId !== undefined && industryId !== null && industryId !== '';

const buildIndustryOptions = (
  fieldIndustries?: CompanyFieldOption[],
  selectedIndustries?: ArticleTemplateIndustry[],
): IndustryOption[] => {
  const optionMap = new Map<string, IndustryOption>();
  const addOption = (industry?: ArticleTemplateIndustry | CompanyFieldOption) => {
    const id = getIndustryId(industry);

    if (!isValidIndustryId(id)) {
      return;
    }

    const value = String(id);
    optionMap.set(value, {
      value,
      label: getIndustryName(industry) || value,
    });
  };

  (fieldIndustries || []).forEach(addOption);
  (selectedIndustries || []).forEach(addOption);

  return Array.from(optionMap.values());
};

const OptionFilterDropdown = ({
  value,
  options,
  placeholder,
  onConfirm,
  confirm,
  clearFilters,
}: {
  value: string[];
  options: IndustryOption[];
  placeholder: string;
  onConfirm: (value: string[]) => void;
} & Pick<FilterDropdownProps, 'confirm' | 'clearFilters'>) => {
  const [tempValue, setTempValue] = useState<string[]>(value);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  return (
    <div style={{ padding: 8, width: 260 }}>
      <Flex vertical gap={8}>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          placeholder={placeholder}
          value={tempValue}
          options={options}
          onChange={setTempValue}
          style={{ width: '100%' }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              onConfirm(tempValue);
              confirm();
            }}
          >
            确定
          </Button>
          <Button
            size="small"
            onClick={() => {
              setTempValue([]);
              onConfirm([]);
              clearFilters?.();
            }}
          >
            重置
          </Button>
        </Space>
      </Flex>
    </div>
  );
};

const ArticleTemplateList: React.FC = () => {
  const [searchParams, setSearchParams] = useSetState({
    pageIndex: 1,
    pageSize: 10,
    templateName: '',
    industryIds: [] as string[],
  });

  const {
    data,
    loading,
    runAsync: refreshList,
  } = useRequest(
    () =>
      articleTemplateApi.list({
        ...searchParams,
        industryIds: searchParams.industryIds.length ? searchParams.industryIds : undefined,
      }),
    {
      refreshDeps: [searchParams],
    }
  );

  const { data: fieldOptions } = useRequest(() => commonApi.companyFieldOptions(ECompanyFieldOptionType.Industry));
  const industryOptions = React.useMemo(() => buildIndustryOptions(fieldOptions || []), [fieldOptions]);

  const handleDelete = (templateIds: string[]) => {
    openDeleteModal(
      {
        content: `确定删除选中的${templateIds.length}个模板吗？`,
        api: () => articleTemplateApi.delete(templateIds as string[]),
      },
      undefined,
      () => {
        setSearchParams({ pageIndex: 1 });
        setSelectedTemplateIds([]);
        setSelectedTemplateMap({});
      }
    );
  };

  const columns: ColumnsType<ArticleTemplateListItem> = [
    {
      title: '模板名称',
      dataIndex: 'templateName',
      render: (text: string) => <EllipsisText text={text} />,
    },
    {
      title: '行业',
      dataIndex: 'industries',
      filtered: searchParams.industryIds.length > 0,
      filterDropdown: ({ confirm, clearFilters }) => (
        <OptionFilterDropdown
          value={searchParams.industryIds}
          options={industryOptions}
          placeholder="筛选行业"
          confirm={confirm}
          clearFilters={clearFilters}
          onConfirm={(value) => setSearchParams({ pageIndex: 1, industryIds: value })}
        />
      ),
      render: (industries: ArticleTemplateListItem['industries']) => {
        const industryText = (industries || [])
          .map(getIndustryName)
          .filter(Boolean)
          .join('、');

        return industryText ? <EllipsisText text={industryText} /> : '-';
      },
    },
    {
      title: '文章数',
      dataIndex: 'articleCount',
    },
    {
      title: '成本',
      dataIndex: 'cost',
      render: (text: string, record) => {
        if (record.minCost === record.maxCost) {
          return `${record.minCost || 0}元`;
        }
        return `${record.minCost || 0}-${record.maxCost || 0}元`;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      render: (time: string) => timeFormatToYearSecond(time),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      render: (text: string) => <EllipsisText text={text} />,
    },
    {
      title: '操作',
      dataIndex: 'operate',
      render: (_: any, record: ArticleTemplateListItem) => (
        <Space>
          <AccessButton permission={['article_template:upsert']}>
            <Button
              type="link"
              size="small"
              onClick={() => {
                openAddOrEditModal(
                  {
                    templateId: record.templateId,
                  },
                  {
                    title: '编辑发文模板',
                  },
                  refreshList
                );
              }}
            >
              编辑
            </Button>
          </AccessButton>
          <AccessButton permission={['article_template:upsert']}>
            <Button
              type="link"
              size="small"
              onClick={() => {
                openAddOrEditModal(
                  {
                    templateId: record.templateId,
                    isCopy: true,
                  },
                  {
                    title: '创建发文模板',
                  },
                  refreshList
                );
              }}
            >
              复制
            </Button>
          </AccessButton>
          <AccessButton permission={['article_template:delete']}>
            <Button type="link" size="small" danger onClick={() => handleDelete([record.templateId?.toString() || ''])}>
              删除
            </Button>
          </AccessButton>
        </Space>
      ),
    },
  ];

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<React.Key[]>([]);
  const [selectedTemplateMap, setSelectedTemplateMap] = useState<Record<string, SelectedArticleTemplate>>({});
  const [replacementDialogOpen, setReplacementDialogOpen] = useState(false);
  const [replacementTemplates, setReplacementTemplates] = useState<SelectedArticleTemplate[]>([]);
  return (
    <div style={{ padding: 24, background: '#fff', height: '100%' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Flex gap={8}>
          <AccessButton permission={['article_template:upsert']}>
            <Button
              type="primary"
              onClick={() => {
                openAddOrEditModal(undefined, undefined, () => {
                  setSearchParams({ pageIndex: 1, templateName: '' });
                });
              }}
            >
              新增模板
            </Button>
          </AccessButton>
          <AccessButton permission={['article_template:media_replace']} allowShow>
            <Button
              type="primary"
              icon={<SwapOutlined />}
              disabled={!selectedTemplateIds.length}
              style={{ cursor: selectedTemplateIds.length ? undefined : 'not-allowed' }}
              onClick={() => {
                if (!selectedTemplateIds.length) {
                  return;
                }
                if (selectedTemplateIds.length > 200) {
                  message.warning('一次最多选择 200 个发文模板');
                  return;
                }
                setReplacementTemplates(
                  selectedTemplateIds.map(
                    (templateId) =>
                      selectedTemplateMap[String(templateId)] || {
                        templateId: String(templateId),
                      }
                  )
                );
                setReplacementDialogOpen(true);
              }}
            >
              一键替换媒体
            </Button>
          </AccessButton>
        </Flex>
        <Flex gap={8} align="center">
          {selectedTemplateIds.length > 0 && (
            <>
              <Button
                onClick={async () => {
                  setSelectedTemplateIds([]);
                  setSelectedTemplateMap({});
                }}
              >
                取消选择({selectedTemplateIds.length})
              </Button>
              <AccessButton permission={['company:article:delete']}>
                <Button
                  type="primary"
                  danger
                  onClick={() => {
                    handleDelete(selectedTemplateIds as string[]);
                  }}
                >
                  批量删除({selectedTemplateIds.length})
                </Button>
              </AccessButton>
            </>
          )}
        </Flex>
        <SearchInput
          style={{ width: '200px', marginLeft: 'auto' }}
          placeholder="请输入模板名称"
          onSearch={(val) => setSearchParams({ templateName: val, pageIndex: 1 })}
        />
      </div>
      <CommonTable
        rowKey="templateId"
        loading={loading}
        columns={columns}
        dataSource={data?.records || []}
        pagination={{
          current: searchParams.pageIndex,
          pageSize: searchParams.pageSize,
          total: data?.total || 0,
          onChange: (page: number, pageSize: number) => setSearchParams({ pageIndex: page, pageSize }),
        }}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedTemplateIds,
          onChange: (selectedRowKeys, selectedRows) => {
            setSelectedTemplateIds(selectedRowKeys);
            setSelectedTemplateMap((current) => {
              const next = { ...current };
              const nextTemplateIds = new Set(selectedRowKeys.map(String));
              Object.keys(next).forEach((templateId) => {
                if (!nextTemplateIds.has(templateId)) {
                  delete next[templateId];
                }
              });
              [...selectedRows, ...(data?.records || [])].forEach((record) => {
                const templateId = String(record.templateId || '');
                if (nextTemplateIds.has(templateId)) {
                  next[templateId] = {
                    templateId,
                    templateName: record.templateName,
                  };
                }
              });
              return next;
            });
          },
        }}
      />
      {replacementDialogOpen && (
        <TemplateMediaReplacementDialog
          open
          selectedTemplates={replacementTemplates}
          onCancel={() => setReplacementDialogOpen(false)}
          onSuccess={refreshList}
        />
      )}
    </div>
  );
};

export default ArticleTemplateList;
const openAddOrEditModal = CommonModalFactory<{
  detail?: ArticleTemplateDetailRes;
  isCopy?: boolean;
}>(
  {
    title: '创建发文模板',
    width: 1280,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const [combinations, setCombinations] = React.useState<ArticleTemplateDetailRes['combinations']>(
      data?.detail?.combinations || [...defaultCombinations]
    );
    const { data: fieldOptions } = useRequest(() =>
      commonApi.companyFieldOptions(ECompanyFieldOptionType.Industry)
    );
    const industryOptions = React.useMemo(
      () => buildIndustryOptions(fieldOptions || [], data?.detail?.industries),
      [fieldOptions, data?.detail?.industries],
    );
    useEffect(() => {
      if (data.detail) {
        form.setFieldsValue({
          templateName: data.detail.templateName,
          industryIds: (data.detail.industryIds || (data.detail.industries || []).map(getIndustryId))
            .filter(isValidIndustryId)
            .map((industryId) => String(industryId))
            .slice(0, 5),
          remark: data.detail.remark,
        });
        setCombinations(data.detail.combinations?.length ? data.detail.combinations : [...defaultCombinations]);
      }
    }, [data]);
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        validateTemplateCombinations(combinations);
        await articleTemplateApi.save({
          templateId: data?.isCopy ? undefined : data.detail?.templateId,
          ...values,
          industryIds: (values.industryIds || []).filter(isValidIndustryId),
          combinations,
        });
        message.success('发文模板保存成功');
      });
    }, [form, data, combinations, setOkFn]);
    return (
      <Form form={form} labelCol={{ span: 5 }}>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item
              name="templateName"
              label="模板名称"
              rules={[
                {
                  required: true,
                  message: '请输入模板名称',
                },
              ]}
            >
              <Input placeholder="请输入模板名称" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="remark" label="备注">
              <Input placeholder="请输入备注" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item label="行业">
              <Form.List name="industryIds">
                {(fields, { add, remove }) => (
                  <Flex wrap gap={8} align="center">
                    {fields.map((field) => (
                      <Form.Item key={field.key} noStyle shouldUpdate>
                        {() => {
                          const selectedIndustryIds = form.getFieldValue('industryIds') || [];
                          const selectedIndustryId = selectedIndustryIds[field.name];
                          const selectedIndustry = industryOptions.find((option) => option.value === selectedIndustryId);

                          return selectedIndustryId ? (
                            <>
                              <Form.Item name={field.name} noStyle hidden>
                                <Input />
                              </Form.Item>
                              <Tag
                                closable
                                onClose={() => remove(field.name)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  height: 32,
                                  marginInlineEnd: 0,
                                  paddingInline: 10,
                                }}
                              >
                                {selectedIndustry?.label || selectedIndustryId}
                              </Tag>
                            </>
                          ) : (
                            <Space.Compact>
                              <Form.Item name={field.name} noStyle>
                                <Select
                                  allowClear
                                  optionFilterProp="label"
                                  options={industryOptions.map((option) => ({
                                    ...option,
                                    disabled:
                                      selectedIndustryIds.includes(option.value) &&
                                      selectedIndustryIds[field.name] !== option.value,
                                  }))}
                                  placeholder="请选择"
                                  showSearch
                                  style={{ width: 180 }}
                                />
                              </Form.Item>
                              <Button icon={<CloseOutlined />} onClick={() => remove(field.name)} />
                            </Space.Compact>
                          );
                        }}
                      </Form.Item>
                    ))}
                    <Button
                      disabled={fields.length >= 5}
                      icon={<PlusOutlined />}
                      onClick={() => {
                        if (fields.length < 5) {
                          add();
                        }
                      }}
                    />
                  </Flex>
                )}
              </Form.List>
            </Form.Item>
          </Col>
        </Row>
        <ArticleTemplateCombinationEditor
          value={combinations}
          onChange={setCombinations}
          mediaBoxScrollY={140}
        />
      </Form>
    );
  },
  async ({ templateId, isCopy, detail } = {} as any) => {
    if (detail) {
      return {
        detail,
        isCopy,
      };
    }
    if (!templateId) {
      return {
        isCopy,
      };
    }
    const detailRes = await articleTemplateApi.get(templateId!);
    const finalDetail = isCopy
      ? {
          ...detailRes,
          templateId: undefined,
          combinations: (detailRes.combinations || []).map((combination) => ({
            ...combination,
            combinationId: undefined,
            items: (combination.items || []).map((item) => ({
              ...item,
              id: undefined,
            })),
          })),
        }
      : detailRes;
    return {
      detail: finalDetail,
      isCopy,
    };
  }
);
